import { ref } from 'vue'
import { getRedirectUrl } from '../utils/auth'
import { salesforceErrorHandler } from '~/utils/errorUtils'
import { isValidTokenResponse, setAuthCookies } from '../utils/authCookies'
import { useUserStore } from '../stores/userStore'

export default function useLogin() {
  const error = ref<null | Error>(null)
  const userStore = useUserStore()

  async function login(email: string, password: string) {
    // A concurrent logout() bumps this while the SLAS call is in flight; a stale success must not
    // resurrect auth cookies/Braze identity a newer generation already cleared.
    const authGeneration = useState<number>('_auth_generation', () => 0)
    const gen = authGeneration.value

    let response
    try {
      const result = await useShopper().helpers.loginRegisteredUserB2C(
        useShopper().shopperLogin,
        { username: email, password },
        {
          redirectURI: getRedirectUrl(),
          usid: userStore.guestUser?.usId,
        },
      )
      if (!isValidTokenResponse(result)) throw new Error('Malformed login response')
      response = result
    } catch (e) {
      response = await salesforceErrorHandler(e)
    }

    // check designs for failed login
    if (response instanceof Error) {
      error.value = response
      return false
    }

    if (authGeneration.value !== gen) return false

    setAuthCookies({
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      expiresIn: response.expires_in,
      refreshTokenExpiresIn: response.refresh_token_expires_in,
    })

    // Braze Integration - Customer Login Tracking
    if (import.meta.client && response.customer_id) {
      const brazeIdentity = useBrazeIdentity()
      // Persist the intent across a torn-down redirect, but emit only after Braze confirms this exact
      // shopper so a shared device cannot attribute the login to the previous profile.
      brazeIdentity.recordLoggedInIntent(response.customer_id)
      void brazeIdentity.ensureIdentity(response.customer_id)
    }

    return true
  }

  async function fetchUser() {
    const authGeneration = useState<number>('_auth_generation', () => 0)
    const gen = authGeneration.value

    const token = await useRefreshTokens().getAccessToken()
    if (authGeneration.value !== gen) return false

    if (!token) return false

    const { customerId, usId } = getIdsFromToken(token)
    if (isGuestToken(token)) {
      userStore.setGuestUser({ customer_id: customerId ?? '', usid: usId ?? '' })
      userStore.setUser(null)
      return true
    } else {
      userStore.setGuestUser(null)
    }

    // Re-affirm on every authenticated load so an interrupted signup/login self-heals. Before the
    // early return below so it runs even when the user is cached; cheap and idempotent once warm.
    if (import.meta.client && customerId) {
      void useBrazeIdentity().ensureIdentity(customerId)
    }

    if (userStore.user?.customerId && userStore.user?.customerId === customerId) return true
    try {
      const headers = await useShopper().getAuthHeaders()

      if (customerId) {
        const response = await useShopper().shopperCustomers.getCustomer({
          headers,
          parameters: {
            customerId: customerId,
          },
        })

        if (authGeneration.value !== gen) return false
        userStore.setUser(response)
      }
      return true
    } catch (e) {
      if (e instanceof Error) error.value = await salesforceErrorHandler(e)
      return false
    }
  }

  /** Each merge handles its own failure, and one failing must not undo the sign-in or skip the rest. */
  async function mergeGuestSession() {
    const basketsStore = useBasketsStore()
    const wishlistStore = useWishlistStore()
    const results = await Promise.allSettled([
      basketsStore.mergeBaskets(),
      wishlistStore.mergeWishlist(),
      useShopStore().mergeMyStore(),
    ])
    for (const result of results) {
      if (result.status === 'rejected') console.error('Failed to merge the guest session:', result.reason)
    }
    const [cart, wishlist] = results
    // Campaign rules now read the account's cart and wishlist, so judge them by the merge, not the Guest's load.
    useLayoutStoresStatus().value = {
      cart: cart.status === 'rejected' || basketsStore.error ? 'failed' : 'ready',
      wishlist: wishlist.status === 'rejected' || wishlistStore.initFailed ? 'failed' : 'ready',
    }
  }

  /**
   * Signs a Guest in to an existing account, a different customer, so their cart, wishlist and My
   * Store are merged into it. Registration keeps the same customer, so it calls `login` instead.
   */
  async function signIn(email: string, password: string) {
    // A Guest load still in flight would land after the merge and overwrite the account's items.
    await whenLayoutStoresSettled()
    const status = useLayoutStoresStatus()
    const guestStatus = { ...status.value }
    // Login switches the Braze user, which can deliver campaigns while the stores are half-swapped.
    status.value = { cart: 'pending', wishlist: 'pending' }
    if (!(await login(email, password))) {
      status.value = guestStatus
      return false
    }
    // Signed in as the account by now, but holding the Guest's items, so neither can be judged.
    if (!(await fetchUser())) {
      status.value = { cart: 'failed', wishlist: 'failed' }
      return false
    }
    await mergeGuestSession()
    return true
  }

  return { fetchUser, login, error, signIn }
}
